import {
  isPatientCancellationAllowed,
  isPatientReschedulingAllowed,
  resolvePatientPortalClinicPolicy,
  type PatientPortalClinicPolicy,
} from '@/lib/bookingPolicies';
import type {
  BookedCourseStatus,
  BookedStepStatus,
  IntervalUnit,
  RemainingStepsBookableBy,
} from '@/types/treatmentCourses';

export type PortalCoursePractitioner = {
  id: string;
  name: string;
};

export type PortalTreatmentCourseStep = {
  id: string;
  sort_order: number;
  appointment_id: string | null;
  service_id: string | null;
  service_name: string;
  title: string | null;
  interval_value: number;
  interval_unit: IntervalUnit;
  interval_tolerance_value: number;
  interval_tolerance_unit: IntervalUnit;
  duration_minutes: number;
  status: BookedStepStatus;
  starts_at: string | null;
  ends_at: string | null;
  practitioner_ids: string[];
  practitioner_names: PortalCoursePractitioner[];
  eligible_practitioners: PortalCoursePractitioner[];
  resource_ids: string[];
};

export type PortalTreatmentCourse = {
  id: string;
  clinic_id: string;
  patient_id: string;
  template_id: string | null;
  template_name: string;
  status: BookedCourseStatus;
  remaining_steps_bookable_by: RemainingStepsBookableBy;
  patient_can_book_remaining: boolean;
  clinic: PatientPortalClinicPolicy;
  steps: PortalTreatmentCourseStep[];
};

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function asString(value: unknown, fallback = ''): string {
  return typeof value === 'string' ? value : fallback;
}

function asNullableString(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null;
}

function asBoolean(value: unknown, fallback: boolean): boolean {
  return typeof value === 'boolean' ? value : fallback;
}

function asInt(value: unknown, fallback: number): number {
  if (typeof value === 'number' && Number.isFinite(value)) return Math.trunc(value);
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return Math.trunc(parsed);
  }
  return fallback;
}

function asStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((item): item is string => typeof item === 'string' && item.length > 0);
}

function asPractitioners(value: unknown): PortalCoursePractitioner[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) => {
      const row = asRecord(item);
      if (!row) return null;
      const id = asString(row.id);
      if (!id) return null;
      return { id, name: asString(row.name, id) };
    })
    .filter((item): item is PortalCoursePractitioner => item != null);
}

function asStepStatus(value: unknown): BookedStepStatus {
  switch (value) {
    case 'unscheduled':
    case 'upcoming':
    case 'completed':
    case 'cancelled':
    case 'no_show':
      return value;
    default:
      return 'unscheduled';
  }
}

function asCourseStatus(value: unknown): BookedCourseStatus {
  switch (value) {
    case 'active':
    case 'completed':
    case 'cancelled':
      return value;
    default:
      return 'active';
  }
}

function asIntervalUnit(value: unknown): IntervalUnit {
  return value === 'weeks' ? 'weeks' : 'days';
}

export function isPatientRemainingBookingEnabled(
  remainingStepsBookableBy: string | null | undefined,
  patientCanBookRemaining?: boolean,
): boolean {
  if (typeof patientCanBookRemaining === 'boolean') return patientCanBookRemaining;
  return remainingStepsBookableBy === 'staff_and_patient';
}

export function portalStepDisplayName(
  step: Pick<PortalTreatmentCourseStep, 'title' | 'service_name'>,
  fallback: string,
): string {
  const title = step.title?.trim();
  if (title) return title;
  const service = step.service_name.trim();
  if (service) return service;
  return fallback;
}

export function previousUnscheduledSteps(
  steps: PortalTreatmentCourseStep[],
  step: PortalTreatmentCourseStep,
): PortalTreatmentCourseStep[] {
  return steps.filter(
    (candidate) =>
      candidate.sort_order < step.sort_order && candidate.status === 'unscheduled',
  );
}

export function canPatientBookStep(
  course: Pick<PortalTreatmentCourse, 'patient_can_book_remaining' | 'remaining_steps_bookable_by' | 'status'>,
  steps: PortalTreatmentCourseStep[],
  step: PortalTreatmentCourseStep,
): boolean {
  if (course.status === 'cancelled') return false;
  if (!isPatientRemainingBookingEnabled(course.remaining_steps_bookable_by, course.patient_can_book_remaining)) {
    return false;
  }
  if (step.status !== 'unscheduled') return false;
  return previousUnscheduledSteps(steps, step).length === 0;
}

export function canPatientRebookStep(
  course: PortalTreatmentCourse,
  step: PortalTreatmentCourseStep,
  now: Date = new Date(),
): boolean {
  if (!step.starts_at || step.status !== 'upcoming') return false;
  if (!isPatientRemainingBookingEnabled(course.remaining_steps_bookable_by, course.patient_can_book_remaining)) {
    return false;
  }
  return isPatientReschedulingAllowed({
    appointmentStatus: 'upcoming',
    startsAt: step.starts_at,
    now,
    allowPatientRescheduling: course.clinic.allowPatientRescheduling,
    deadlineHours: course.clinic.deadlineHours,
  });
}

export function canPatientCancelCourseStep(
  course: PortalTreatmentCourse,
  step: PortalTreatmentCourseStep,
  now: Date = new Date(),
): boolean {
  if (!step.appointment_id || !step.starts_at || step.status !== 'upcoming') return false;
  return isPatientCancellationAllowed({
    appointmentStatus: 'upcoming',
    startsAt: step.starts_at,
    now,
    deadlineHours: course.clinic.deadlineHours,
    beforeDeadline: course.clinic.beforeDeadline,
    afterDeadline: course.clinic.afterDeadline,
  });
}

export function firstBookableUnscheduledStep(
  course: PortalTreatmentCourse,
): PortalTreatmentCourseStep | null {
  const ordered = [...course.steps].sort((a, b) => a.sort_order - b.sort_order);
  return ordered.find((step) => canPatientBookStep(course, ordered, step)) ?? null;
}

export function previousBookedStartsAt(
  steps: PortalTreatmentCourseStep[],
  step: PortalTreatmentCourseStep,
): string | null {
  const previous = [...steps]
    .filter(
      (candidate) =>
        candidate.sort_order < step.sort_order &&
        candidate.starts_at &&
        (candidate.status === 'upcoming' ||
          candidate.status === 'completed' ||
          candidate.status === 'no_show'),
    )
    .sort((a, b) => b.sort_order - a.sort_order);
  return previous[0]?.starts_at ?? null;
}

export function parsePortalTreatmentCourse(value: unknown): PortalTreatmentCourse | null {
  const row = asRecord(value);
  if (!row) return null;
  const id = asString(row.id);
  const clinicId = asString(row.clinic_id);
  const patientId = asString(row.patient_id);
  if (!id || !clinicId || !patientId) return null;

  const clinicRaw = asRecord(row.clinic);
  const clinic = resolvePatientPortalClinicPolicy({
    cancellation_deadline_hours: asInt(clinicRaw?.cancellation_deadline_hours, 24),
    allow_patient_rescheduling: asBoolean(clinicRaw?.allow_patient_rescheduling, false),
    patient_cancel_before_deadline: asString(clinicRaw?.patient_cancel_before_deadline, 'allow'),
    patient_cancel_after_deadline: asString(clinicRaw?.patient_cancel_after_deadline, 'deny'),
  });

  const steps = (Array.isArray(row.steps) ? row.steps : [])
    .map((item): PortalTreatmentCourseStep | null => {
      const step = asRecord(item);
      if (!step) return null;
      const stepId = asString(step.id);
      if (!stepId) return null;
      return {
        id: stepId,
        sort_order: asInt(step.sort_order, 0),
        appointment_id: asNullableString(step.appointment_id),
        service_id: asNullableString(step.service_id),
        service_name: asString(step.service_name),
        title: asNullableString(step.title),
        interval_value: asInt(step.interval_value, 0),
        interval_unit: asIntervalUnit(step.interval_unit),
        interval_tolerance_value: asInt(step.interval_tolerance_value, 0),
        interval_tolerance_unit: asIntervalUnit(step.interval_tolerance_unit),
        duration_minutes: asInt(step.duration_minutes, 0),
        status: asStepStatus(step.status),
        starts_at: asNullableString(step.starts_at),
        ends_at: asNullableString(step.ends_at),
        practitioner_ids: asStringArray(step.practitioner_ids),
        practitioner_names: asPractitioners(step.practitioner_names),
        eligible_practitioners: asPractitioners(step.eligible_practitioners),
        resource_ids: asStringArray(step.resource_ids),
      };
    })
    .filter((item): item is PortalTreatmentCourseStep => item != null)
    .sort((a, b) => a.sort_order - b.sort_order);

  return {
    id,
    clinic_id: clinicId,
    patient_id: patientId,
    template_id: asNullableString(row.template_id),
    template_name: asString(row.template_name),
    status: asCourseStatus(row.status),
    remaining_steps_bookable_by:
      row.remaining_steps_bookable_by === 'staff_and_patient' ? 'staff_and_patient' : 'staff_only',
    patient_can_book_remaining: isPatientRemainingBookingEnabled(
      asString(row.remaining_steps_bookable_by),
      asBoolean(row.patient_can_book_remaining, row.remaining_steps_bookable_by === 'staff_and_patient'),
    ),
    clinic,
    steps,
  };
}

export function parsePortalTreatmentCourseList(value: unknown): PortalTreatmentCourse[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) => parsePortalTreatmentCourse(item))
    .filter((item): item is PortalTreatmentCourse => item != null);
}
